import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Translate } from '@phosphor-icons/react';
import { useLocale } from '@/i18n';
import { LOCALE_DISPLAY_NAMES } from '@/i18n';
import type { SupportedLocale } from '@/i18n';
import { Dropdown } from './Dropdown';
import type { DropdownItem } from './Dropdown';
import { cn } from '../../utils/cn';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LanguageSwitcherProps {
  /** Visual variant */
  variant?: 'icon' | 'full';
  /** Additional CSS classes applied to the outer wrapper */
  className?: string;
}

// ---------------------------------------------------------------------------
// Locale flag labels (short codes shown in the trigger for "full" variant)
// ---------------------------------------------------------------------------

const LOCALE_SHORT_LABELS: Record<SupportedLocale, string> = {
  'pt-BR': 'PT',
  'en-US': 'EN',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Language switcher button/dropdown.
 *
 * Uses Phosphor `Translate` icon as trigger. Two variants:
 * - `icon` (default): Shows only the Translate icon. Compact, for headers/toolbars.
 * - `full`: Shows Translate icon + current language short code (e.g. "PT", "EN").
 *
 * Renders a dropdown with all supported locales. Selecting a locale:
 * 1. Updates the react-intl context (triggers re-render of all translated strings)
 * 2. Persists the choice to localStorage
 * 3. Updates the HTML `lang` attribute
 *
 * Accessible: Uses existing Dropdown component which provides keyboard navigation,
 * focus management, and ARIA attributes.
 */
export function LanguageSwitcher({
  variant = 'icon',
  className,
}: LanguageSwitcherProps) {
  const { locale, setLocale, supportedLocales } = useLocale();
  const intl = useIntl();

  const handleSelect = useCallback(
    (value: string) => {
      setLocale(value as SupportedLocale);
    },
    [setLocale],
  );

  const items: DropdownItem[] = supportedLocales.map((loc) => ({
    value: loc,
    label: `${LOCALE_DISPLAY_NAMES[loc] ?? loc}${loc === locale ? ' ✓' : ''}`,
  }));

  const ariaLabel = intl.formatMessage(
    { id: 'a11y.currentLanguage' },
    { language: LOCALE_DISPLAY_NAMES[locale] ?? locale },
  );

  const trigger = (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2 py-1.5 rounded-lg',
        'text-text-secondary hover:text-text-primary hover:bg-bg-hover',
        'transition-colors cursor-pointer',
        'min-h-[44px] min-w-[44px] lg:min-h-0 lg:min-w-0',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus',
      )}
      aria-label={ariaLabel}
    >
      <Translate size={20} weight="regular" />
      {variant === 'full' && (
        <span className="text-sm font-medium">
          {LOCALE_SHORT_LABELS[locale] ?? locale}
        </span>
      )}
    </span>
  );

  return (
    <Dropdown
      trigger={trigger}
      items={items}
      onSelect={handleSelect}
      align="right"
      className={className}
    />
  );
}
